import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { remoteConfig } from '../../utility/firebaseRemoteConfigHelper';
import { useAuth } from '../../auth/firebaseAuth';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import {
  Loader2,
  TrendingUp,
  Home,
  Timer,
  ShieldCheck,
  CheckCircle2,
  MapPin,
  Euro,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Helmet } from 'react-helmet';
import valuationBackground from './assets/valuation-landing-background.png';

interface ValuationResult {
  estimatedValue: number;
  minPrice: number;
  maxPrice: number;
  confidence: number;
  currency: string;
  metadata: {
    routingKey: string;
    sampleSize: number;
    calculationDate: string;
    disclaimer: string;
  };
}

interface MarketStats {
  medianPrice: number;
  yoyChange: number;
  salesVolume: number;
  recentSales: Array<{
    address: string;
    price: number;
    date: string;
  }>;
}

interface RelatedLocation {
  name: string;
  slug: string;
  type: string;
}

const HomeValuationPage: React.FC = () => {
  const navigate = useNavigate();
  const { county, routingKey, town } = useParams();
  const { currentUser } = useAuth();
  const analytics = useAnalyticsWithConsent();

  // State
  const [eircode, setEircode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ValuationResult | null>(null);

  // pSEO State
  const [marketStats, setMarketStats] = useState<MarketStats | null>(null);
  const [areaInsights, setAreaInsights] = useState<any | null>(null);
  const [relatedLocations, setRelatedLocations] = useState<{
    parent: RelatedLocation | null;
    children: RelatedLocation[];
    siblings: RelatedLocation[];
  } | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Derived Location Name
  const getLocationName = () => {
    if (county) return `County ${county.charAt(0).toUpperCase() + county.slice(1)}`;
    if (town)
      return town
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    if (routingKey) return `Eircode ${routingKey.toUpperCase()}`;
    return 'Ireland';
  };

  const getSlug = () => {
    if (county) return county;
    if (town) return town;
    if (routingKey) return routingKey;
    return null;
  };

  const locationName = getLocationName();
  const slug = getSlug();
  const isPseoPage = !!slug;

  useEffect(() => {
    analytics.trackPixelViewContent({
      content_name: 'Home Valuation Tool',
      content_ids: ['home-valuation-tool'],
      content_type: 'service',
    });
  }, [locationName]);

  useEffect(() => {
    // Check Remote Config
    try {
      if (!remoteConfig.isHomeValuationToolEnabled()) {
        toast.error('Home valuation feature is currently unavailable. Redirecting to home...');
        setTimeout(() => navigate('/'), 1500);
      }
    } catch (err) {
      toast.error('Home valuation feature is currently unavailable. Redirecting to home...');
      setTimeout(() => navigate('/'), 1500);
    }
  }, [navigate]);

  // Fetch Market Stats for pSEO pages
  useEffect(() => {
    if (!slug) return;

    const fetchStats = async () => {
      setLoadingStats(true);
      try {
        // Parallel fetch for stats, insights, and related locations
        const [statsRes, insightsRes, relatedRes] = await Promise.all([
          fetch(`${CLOUD_FUNCTIONS_BASE_URL}/getMarketStats?area=${slug}`),
          fetch(`${CLOUD_FUNCTIONS_BASE_URL}/getAreaInsights?slug=${slug}`),
          fetch(`${CLOUD_FUNCTIONS_BASE_URL}/getRelatedLocations?slug=${slug}`),
        ]);

        if (statsRes.ok) {
          const data = await statsRes.json();
          if (data.success) setMarketStats(data.stats);
        }

        if (insightsRes.ok) {
          const data = await insightsRes.json();
          if (data.success) setAreaInsights(data.insights);
        }

        if (relatedRes.ok) {
          const data = await relatedRes.json();
          if (data.success) setRelatedLocations(data.related);
        }
      } catch (error) {
        console.error('Failed to fetch local stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, [slug]);

  const handleValuation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eircode) {
      toast.error('Please enter your Eircode');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/getAvmEstimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eircode }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get valuation');
      }

      setResult(data.data);

      analytics.trackPixelCustom('valuation_generated', {
        estimated_value: data.data.estimatedValue,
        currency: data.data.currency,
        min_price: data.data.minPrice,
        max_price: data.data.maxPrice,
      });

      toast.success('Valuation ready');
    } catch (err) {
      toast.error('Unable to generate valuation');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <>
      <Helmet>
        <title>
          {isPseoPage
            ? `Free Home Valuation for ${locationName} | Property Estimate`
            : 'Free Home Valuation | Instant Property Estimate Ireland'}
        </title>
        <meta
          name="description"
          content={
            isPseoPage
              ? `Get a free instant property valuation for ${locationName}. See recent sales, median prices (€${marketStats?.medianPrice ? (marketStats.medianPrice / 1000).toFixed(0) + 'k' : '...'}) and market trends.`
              : 'Get a free instant property valuation for your Irish home. Estimate based on the Property Price Register (PPR). Not a certified valuation. Fast, accurate, and no obligation.'
          }
        />
        <meta
          name="keywords"
          content={`home valuation ${locationName}, property prices ${locationName}, house value ${locationName}, sell house ${locationName}`}
        />
        <link rel="canonical" href={`https://rentsmart.ie/valuation/${slug || 'home-valuation'}`} />
      </Helmet>

      <div
        className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visible min-h-screen bg-cover bg-center bg-no-repeat font-sans"
        style={{ backgroundImage: `url(${valuationBackground})` }}
      >
        <div className="min-h-screen bg-gradient-to-b from-transparent via-white/50 to-white/95">
          {/* Main Hero Section */}
          {!result && (
            <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-20 sm:pt-32 pb-8 sm:pb-12 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="inline-block mb-3 sm:mb-4 px-3 sm:px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-semibold text-xs sm:text-sm tracking-wide uppercase shadow-sm">
                #1 Property Valuation Tool in Ireland
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-4 sm:mb-6 tracking-tight drop-shadow-sm leading-tight px-2">
                {isPseoPage ? (
                  <>
                    Property Valuation for{' '}
                    <span className="text-[#1B365D] block sm:inline">{locationName}</span>
                  </>
                ) : (
                  <>
                    Discover Your Home's <br className="hidden md:block" />
                    <span className="text-[#1B365D]">True Value</span> Instantly
                  </>
                )}
              </h1>

              <p className="text-lg sm:text-xl md:text-2xl text-slate-700 mb-8 sm:mb-12 font-medium max-w-2xl mx-auto leading-relaxed px-2">
                {marketStats ? (
                  <>
                    Current median price in <strong>{locationName}</strong> is{' '}
                    <strong>{formatCurrency(marketStats.medianPrice)}</strong>.
                    {marketStats.yoyChange !== 0 && (
                      <span
                        className={`block text-base mt-1 ${marketStats.yoyChange > 0 ? 'text-green-600' : 'text-red-500'}`}
                      >
                        Market is {marketStats.yoyChange > 0 ? 'up' : 'down'}{' '}
                        {Math.abs(marketStats.yoyChange)}% since last year.
                      </span>
                    )}
                  </>
                ) : (
                  'Free property estimate based on the Property Price Register.'
                )}
              </p>

              {/* Input Form */}
              <form
                onSubmit={handleValuation}
                className="max-w-xl mx-auto shadow-2xl rounded-lg overflow-hidden flex flex-col sm:flex-row mb-8 sm:mb-12 relative z-10"
              >
                <div className="flex-1 bg-white relative">
                  <input
                    value={eircode}
                    onChange={(e) => setEircode(e.target.value)}
                    placeholder="Enter Your Eircode"
                    className="w-full h-full px-4 sm:px-6 py-3 sm:py-4 text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-blue-50 transition-colors text-base sm:text-lg"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#1B365D] hover:bg-[#152a48] text-white px-6 sm:px-8 py-3 sm:py-4 font-semibold text-base sm:text-lg flex items-center justify-center transition-colors disabled:opacity-80 sm:w-auto w-full min-h-[48px]"
                >
                  {loading ? (
                    <Loader2 className="animate-spin h-5 w-5 sm:h-6 sm:w-6" />
                  ) : (
                    'Get My Valuation'
                  )}
                </button>
              </form>
            </section>
          )}

          {/* Result State */}
          {result && (
            <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-20 space-y-6 sm:space-y-8 animate-in fade-in zoom-in-95 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-8 items-start relative z-10">
                <div className="md:col-span-8 md:col-start-3">
                  <div className="bg-[#1e7e46] rounded-xl shadow-2xl overflow-hidden mb-6 sm:mb-8 transform transition-transform hover:scale-[1.01]">
                    <div className="bg-[#268f53] py-2 text-center border-b border-white/10">
                      <span className="text-white font-semibold tracking-wide text-xs sm:text-sm uppercase">
                        Estimated Value ({locationName})
                      </span>
                    </div>
                    <div className="p-6 sm:p-8 text-center">
                      <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-2 sm:mb-3 tracking-tight drop-shadow-sm">
                        {formatCurrency(result.estimatedValue)}
                      </div>
                      <p className="text-green-100 text-xs sm:text-sm font-medium opacity-90">
                        Based on current market analysis
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8">
                    <button
                      onClick={() => {
                        if (currentUser) {
                          navigate('/seller-checklist');
                        } else {
                          navigate('/signup?returnUrl=/seller-checklist');
                        }
                      }}
                      className="bg-[#1B365D] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg shadow-xl hover:bg-[#152a48] hover:shadow-2xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 min-w-[200px] min-h-[48px]"
                    >
                      List Your Home Now
                      <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                    <button
                      onClick={() => setResult(null)}
                      className="bg-white border-2 border-slate-200 text-slate-700 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-all min-h-[48px]"
                    >
                      Check Another Property
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* pSEO Content: Market Stats & Sales (Visible if Stats Loaded) */}
          {marketStats && !result && (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 text-center">
                  <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-[#1B365D]">
                    <Euro className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Median Price</h3>
                  <p className="text-3xl font-bold text-[#1B365D]">
                    {formatCurrency(marketStats.medianPrice)}
                  </p>
                </div>
                {/* <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 text-center">
                  <div className="bg-green-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Year-over-Year</h3>
                  <p
                    className={`text-3xl font-bold ${marketStats.yoyChange >= 0 ? 'text-green-600' : 'text-red-500'}`}
                  >
                    {marketStats.yoyChange > 0 ? '+' : ''}
                    {marketStats.yoyChange}%
                  </p>
                </div> */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 text-center">
                  <div className="bg-purple-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-purple-600">
                    <Home className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Sales Volume</h3>
                  <p className="text-3xl font-bold text-[#1B365D]">
                    {marketStats.salesVolume}{' '}
                    <span className="text-base font-normal text-slate-500">sold (last 12m)</span>
                  </p>
                </div>
              </div>

              {/* Recent Sales Table */}
              <div className="bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden mb-12">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                  <h3 className="text-xl font-bold text-slate-900">
                    Recent Sales in {locationName}
                  </h3>
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Source: PPR
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold">
                      <tr>
                        <th className="px-6 py-3">Address</th>
                        <th className="px-6 py-3">Date</th>
                        <th className="px-6 py-3 text-right">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {marketStats.recentSales.map((sale, i) => (
                        <tr key={i} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-medium text-slate-900">{sale.address}</td>
                          <td className="px-6 py-4">{sale.date}</td>
                          <td className="px-6 py-4 text-right font-bold text-[#1B365D]">
                            {formatCurrency(sale.price)}
                          </td>
                        </tr>
                      ))}
                      {marketStats.recentSales.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-slate-400">
                            No recent public sales records found for this area.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Area Insights (if available) */}
              {areaInsights && (
                <div className="bg-white rounded-xl shadow-md border border-slate-100 p-8 mb-12">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">
                    Living in {locationName}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-[#1B365D] mb-2 flex items-center gap-2">
                        <MapPin className="h-5 w-5" /> Area Vibe
                      </h4>
                      <p className="text-slate-600 leading-relaxed mb-6">
                        {areaInsights.areaVibe?.feel ||
                          'An excellent location with great connectivity and amenities.'}
                      </p>

                      <h4 className="font-semibold text-[#1B365D] mb-2 flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5" /> Safety & Community
                      </h4>
                      <p className="text-slate-600 leading-relaxed">
                        {areaInsights.communityProfile?.neighbourInteraction ||
                          areaInsights.areaVibe?.safetyPerception}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1B365D] mb-2 flex items-center gap-2">
                        <Home className="h-5 w-5" /> Housing Reality
                      </h4>
                      <p className="text-slate-600 leading-relaxed mb-6">
                        {areaInsights.housingReality?.insight ||
                          areaInsights.housingReality?.dominantType}
                      </p>

                      <h4 className="font-semibold text-[#1B365D] mb-2 flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" /> Why live here?
                      </h4>
                      <ul className="list-disc list-inside text-slate-600 space-y-1">
                        {areaInsights.livingProsCons?.pros
                          ?.slice(0, 3)
                          .map((pro: string, idx: number) => (
                            <li key={idx}>{pro}</li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Internal Linking / Nearby Areas */}
              {relatedLocations &&
                (relatedLocations.children.length > 0 || relatedLocations.siblings.length > 0) && (
                  <div className="mt-12">
                    <h4 className="text-lg font-bold text-slate-900 mb-4">Explore Nearby Areas</h4>
                    <div className="flex flex-wrap gap-2 text-sm text-slate-600">
                      {relatedLocations.parent && (
                        <Link
                          to={`/valuation/${relatedLocations.parent.slug}`}
                          className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors font-medium"
                        >
                          Back to {relatedLocations.parent.name}
                        </Link>
                      )}
                      {relatedLocations.siblings.map((loc) => (
                        <Link
                          key={loc.slug}
                          to={`/valuation/${loc.slug}`}
                          className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full hover:bg-slate-200 transition-colors"
                        >
                          Valuation in {loc.name}
                        </Link>
                      ))}
                      {relatedLocations.children.map((loc) => (
                        <Link
                          key={loc.slug}
                          to={`/valuation/${loc.slug}`}
                          className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full hover:bg-slate-200 transition-colors"
                        >
                          {loc.type === 'eircode' ? `Eircode ${loc.name}` : `Valuation ${loc.name}`}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
            </section>
          )}

          {!result && !marketStats && (
            <div className="flex justify-center">
              <div className="flex flex-col sm:inline-flex sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-6 md:gap-12 text-sm sm:text-base text-slate-800 font-semibold sm:bg-white/80 sm:px-8 sm:py-4 sm:rounded-full sm:shadow-sm max-w-sm sm:max-w-none mx-auto mb-16 relative z-10 px-4">
                <div className="flex items-center gap-2 bg-white/80 px-4 py-3 rounded-full shadow-sm sm:bg-transparent sm:px-0 sm:py-0 sm:shadow-none">
                  <Timer className="h-4 w-4 sm:h-5 sm:w-5 text-[#1B365D]" />
                  <span>Fast & Free</span>
                </div>
                <div className="hidden sm:block w-px h-6 bg-slate-300"></div>
                <div className="flex items-center gap-2 bg-white/80 px-4 py-3 rounded-full shadow-sm sm:bg-transparent sm:px-0 sm:py-0 sm:shadow-none">
                  <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 text-[#1B365D]" />
                  <span>Accurate Estimate</span>
                </div>
                <div className="hidden sm:block w-px h-6 bg-slate-300"></div>
                <div className="flex items-center gap-2 bg-white/80 px-4 py-3 rounded-full shadow-sm sm:bg-transparent sm:px-0 sm:py-0 sm:shadow-none">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-[#1B365D]" />
                  <span>No Obligation</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default HomeValuationPage;
